import { get } from 'lodash';

export const INNER_WHITESPACE_ERROR = 'Whitespace is not allowed inside this value';

/**
 * Removes leading and trailing whitespace. Non-string values are returned untouched so the
 * helper can be applied blindly to any form value.
 */
export const trimEdges = <T>(value: T): T => (typeof value === 'string' ? (value.trim() as T) : value);

/**
 * True when the value still contains whitespace once the edges are trimmed, e.g. `'AB CD'`.
 */
export const hasInnerWhitespace = (value: unknown): boolean => typeof value === 'string' && /\s/.test(value.trim());

/**
 * Predicate for zod `.refine(...)` on fields that may not contain whitespace inside the value.
 */
export const noInnerWhitespace = (value: string): boolean => !hasInnerWhitespace(value);

interface TrimmableForm<TValues, TProps> {
  values: TValues;
  getInputProps: (path: string) => TProps;
  setFieldValue: (path: string, value: unknown) => void;
}

/**
 * Input props that drop the leading/trailing whitespace of a field as soon as the user leaves it,
 * so the cleaned value is what gets validated and saved.
 */
export const trimOnBlurProps = <TValues, TProps extends { onBlur?: (event: never) => void }>(form: TrimmableForm<TValues, TProps>, path: string): TProps => {
  const inputProps = form.getInputProps(path);

  return {
    ...inputProps,
    onBlur: (event: never) => {
      form.setFieldValue(path, trimEdges(get(form.values, path)));
      inputProps.onBlur?.(event);
    },
  } as TProps;
};
